import { useContext } from '@nuxtjs/composition-api';
import type { Cart } from '@wemade-vsf/magento-api';
import { isOciCookieName, ociParamsCookieName } from '../constants';
import { cartGetters } from '@wemade-vsf/checkout'
import { useCustomerPricing } from '@wemade-vsf/catalog/lib/runtime/product/composables/useCustomerPricing';
import { Logger } from '@wemade-vsf/core';

type OciParams = {
  hookUrl: string;
  returnTarget: string;
}

export type OciFormData = {
  hookUrl: string;
  returnTarget: string;
  fields: Array<{
    name: string;
    value: string;
  }>
}

export function useOciCart() {
  const { app, $config } = useContext();
  const cookieDefaultOpts = $config.cookiesDefaultOpts || {};
  const isOciContext = !!app.$cookies.get(isOciCookieName, cookieDefaultOpts);

  const getOciParams = () => {
    const data = app.$cookies.get<OciParams | null | undefined>(ociParamsCookieName, cookieDefaultOpts);
    if (typeof data === 'string') {
      return JSON.parse(data);
    }
    return data;
  }

  const { displayPricesIncludingTax: arePricesIncludingTax } = useCustomerPricing();

  function getFormData(cart: Cart): OciFormData {
    const params = getOciParams();
    Logger.debug('OCI params', params);

    if (!params) {
      throw new Error('OCI params not found');
    }
    const fields = cart.items?.flatMap((item, index) => {
      if (!item?.product) {
        return [];
      }
      const itemPrice = cartGetters.getItemPrice(item, arePricesIncludingTax.value);
      const finalPrice = itemPrice.special ? itemPrice.special : itemPrice.regular
      const qty = cartGetters.getItemQty(item)
      const matgroup = item.product?.wm_custom_attributes?.find(attr => attr.attribute_code === 'wm_unspcs')?.value;
      return [
        {
          name: `NEW_ITEM-DESCRIPTION[${index + 1}]`,
          value: cartGetters.getItemName(item)
        },
        {
          name: `NEW_ITEM-QUANTITY[${index + 1}]`,
          value: `${qty}`
        },
        {
          name: `NEW_ITEM-CURRENCY[${index + 1}]`,
          value: app.$vsf.$magento.config.state.getCurrency()
        },
        {
          name: `NEW_ITEM-PRICE[${index + 1}]`,
          value: `${finalPrice ? (finalPrice / qty) : 0}`
        },
        {
          name: `NEW_ITEM-UNIT[${index + 1}]`,
          value: 'PCE'
        },
        {
          name: `NEW_ITEM-VENDORMAT[${index + 1}]`,
          value: cartGetters.getItemSku(item)
        },
        ...matgroup ? [
          {
            name: `NEW_ITEM-MATGROUP[${index + 1}]`,
            value: matgroup
          }
        ] : []
      ]
    })

    return {
      ...params,
      fields
    }
  }

  return {
    isOciContext,
    arePricesIncludingTax,
    getOciParams,
    getFormData
  }
}